import React, { useMemo } from 'react';
import { useTranslation } from 'src/hooks/useTranslation';

interface UseContentTranslationOptions {
  skipTranslation?: boolean;
}

/**
 * Extracts text content from JSX that only contains text and <i>/<b> tags
 * Returns the plain text with <i> and <b> tags preserved as strings
 * Trims leading and trailing whitespace from the final result
 */
const extractTextFromSimpleJSX = (node: React.ReactNode): string | null => {
  if (typeof node === 'string') {
    return node;
  }

  // Handle arrays of children (mixed text + JSX elements)
  if (Array.isArray(node)) {
    const texts = node.map((child) => extractTextFromSimpleJSX(child));
    if (texts.every((text) => text !== null)) {
      return texts.join('').trim();
    }
    return null;
  }

  if (React.isValidElement(node)) {
    const element = node as React.ReactElement;

    // Handle React Fragment
    if (element.type === React.Fragment) {
      return extractTextFromSimpleJSX(React.Children.toArray(element.props.children));
    }

    // Only handle <i> and <b> tags
    if (element.type === 'i' || element.type === 'b') {
      const childText = extractTextFromSimpleJSX(element.props.children);
      if (childText !== null) {
        return `<${element.type}>${childText}</${element.type}>`;
      }
    }
  }

  return null;
};

/**
 * Converts a translated string with <i>/<b>/<a> tags back to JSX
 */
const parseTextToJSX = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  
  // Match <i>, <b>, and <a href="..."> tags
  const regex = /<(i|b)>(.*?)<\/\1>|<a\s+href=["']([^"']+)["']>(.*?)<\/a>/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the tag
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }

    if (match[1]) {
      // <i> or <b> tag
      const tag = match[1];
      const content = match[2];
      parts.push(tag === 'i' ? <i key={match.index}>{content}</i> : <b key={match.index}>{content}</b>);
    } else if (match[3]) {
      // <a href="..."> tag
      const href = match[3];
      const content = match[4];
      parts.push(
        <a key={match.index} href={href} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      );
    }

    currentIndex = regex.lastIndex;
  }

  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(text.substring(currentIndex));
  }

  return parts.length === 0 ? text : parts.length === 1 ? parts[0] : <>{parts}</>;
};

/**
 * Hook for translating content (text or children) in components
 * Used by Typography, Button, and other components that display translatable text
 */
export const useContentTranslation = (
  content: React.ReactNode | string | undefined,
  options: UseContentTranslationOptions = {}
): React.ReactNode => {
  const { t } = useTranslation();
  const { skipTranslation = false } = options;

  const processedContent = useMemo(() => {
    // If translation is skipped locally, return as-is
    if (skipTranslation) {
      return content;
    }

    if (!content) {
      return content;
    }

    // Handle string content
    if (typeof content === 'string') {
      // Trim the content to match how translation keys are stored
      const trimmedContent = content.trim();
      const translatedText = t(trimmedContent);
      
      // Check if the string contains <i>, <b>, or <a> tags and parse them
      if (translatedText.includes('<i>') || translatedText.includes('<b>') || translatedText.includes('<a ')) {
        return parseTextToJSX(translatedText);
      }
      
      return translatedText;
    }

    // Try to extract text from JSX with <i> and <b> tags
    const extractedText = extractTextFromSimpleJSX(content);
    if (extractedText !== null) {
      const translatedText = t(extractedText);
      // If translation exists and is different, parse it back to JSX
      if (translatedText !== extractedText) {
        return parseTextToJSX(translatedText);
      }
      // If no translation, return original JSX
      return content;
    }

    // For other JSX, return as-is
    return content;
  }, [content, skipTranslation, t]);

  return processedContent;
};
